import { Image, StyleSheet, Text, View } from 'react-native';
import { fontFamilies } from '../../../utils/fontLoader';

// Shared car image for listing card thumbnails
const LISTING_IMAGE = require('../../../../assets/images/landing/responsive/hero-card/small.webp');

const MatchBadge = ({ percent }) => {
  const isGreen = percent >= 80;
  const color = isGreen ? '#0BFD4C' : '#FD800C';
  const bg = isGreen ? 'rgba(11,253,76,0.2)' : 'rgba(253,128,12,0.2)';

  return (
    <View style={[listingStyles.matchBadge, { backgroundColor: bg }]}>
      <Text style={[listingStyles.matchText, { color }]}>{percent}%</Text>
    </View>
  );
};

const MainListingCard = ({ listing }) => (
  <View style={listingStyles.mainCard}>
    <View style={listingStyles.mainImageWrap}>
      <Image source={listing.image || LISTING_IMAGE} style={listingStyles.mainImage} resizeMode="cover" />
      {listing.badge && (
        <View style={listingStyles.badge}>
          <Text style={listingStyles.badgeText}>{listing.badge}</Text>
        </View>
      )}
    </View>
    <View style={listingStyles.mainContent}>
      <Text style={listingStyles.mainTitle} numberOfLines={1}>{listing.title}</Text>
      <Text style={listingStyles.mainSpecs} numberOfLines={1}>{listing.specs}</Text>
      <Text style={listingStyles.mainPrice}>{listing.price}</Text>
      <View style={listingStyles.mainFooter}>
        <Text style={listingStyles.mainHighlight} numberOfLines={1}>{listing.highlight}</Text>
        <MatchBadge percent={listing.matchPercent} />
      </View>
    </View>
  </View>
);

const CompactListingCard = ({ listing }) => (
  <View style={listingStyles.compactCard}>
    <View style={listingStyles.compactContent}>
      <Text style={listingStyles.compactTitle} numberOfLines={1}>{listing.title}</Text>
      <Text style={listingStyles.compactSpecs} numberOfLines={1}>{listing.price} · {listing.specs}</Text>
    </View>
  </View>
);

const ListingStack = ({ listings }) => (
  <View style={stackStyles.container}>
    <View style={[stackStyles.row, stackStyles.tinyTop]}>
      <CompactListingCard listing={listings[4]} />
    </View>
    <View style={[stackStyles.row, stackStyles.smallTop]}>
      <CompactListingCard listing={listings[1]} />
    </View>
    <View style={[stackStyles.row, stackStyles.main]}>
      <MainListingCard listing={listings[0]} />
    </View>
    <View style={[stackStyles.row, stackStyles.smallBottom]}>
      <CompactListingCard listing={listings[2]} />
    </View>
    <View style={[stackStyles.row, stackStyles.tinyBottom]}>
      <CompactListingCard listing={listings[3]} />
    </View>
  </View>
);

const Card = ({ step }) => {
  if (step.listings) {
    return <ListingStack listings={step.listings} />;
  }

  return (
    <View style={styles.card}>
      <Image source={step.image} style={styles.image} resizeMode="cover" />
    </View>
  );
};

const listingStyles = StyleSheet.create({
  matchBadge: {
    borderRadius: 99,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  matchText: {
    fontFamily: fontFamilies.satoshi.bold,
    fontSize: 12,
  },
  // Main card
  mainCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    overflow: 'hidden',
    flexDirection: 'row',
    width: '100%',
    height: 140,
  },
  mainImageWrap: {
    width: 140,
    height: 140,
    position: 'relative',
  },
  mainImage: {
    width: '100%',
    height: '100%',
  },
  badge: {
    position: 'absolute',
    top: 8,
    left: 8,
    backgroundColor: '#FF7B00',
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  badgeText: {
    fontFamily: fontFamilies.satoshi.bold,
    fontSize: 10,
    color: '#FFFFFF',
    letterSpacing: 0.5,
  },
  mainContent: {
    flex: 1,
    padding: 16,
    justifyContent: 'center',
    gap: 3,
  },
  mainTitle: {
    fontFamily: fontFamilies.satoshi.medium,
    fontSize: 15,
    color: '#0E0E0E',
    lineHeight: 20,
  },
  mainSpecs: {
    fontFamily: fontFamilies.satoshi.regular,
    fontSize: 12,
    color: 'rgba(14,14,14,0.5)',
    lineHeight: 16,
  },
  mainPrice: {
    fontFamily: fontFamilies.satoshi.bold,
    fontSize: 18,
    color: '#0E0E0E',
    lineHeight: 24,
    marginTop: 2,
  },
  mainFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  mainHighlight: {
    fontFamily: fontFamilies.satoshi.medium,
    fontSize: 11,
    color: '#10B83D',
    flex: 1,
    marginRight: 8,
  },
  // Compact card
  compactCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    overflow: 'hidden',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    height: 70,
  },
  compactContent: {
    flex: 1,
    paddingHorizontal: 12,
  },
  compactTitle: {
    fontFamily: fontFamilies.satoshi.medium,
    fontSize: 12,
    color: '#0E0E0E',
    lineHeight: 16,
  },
  compactSpecs: {
    fontFamily: fontFamilies.satoshi.regular,
    fontSize: 10,
    color: 'rgba(14,14,14,0.5)',
    lineHeight: 14,
  },
});

const stackStyles = StyleSheet.create({
  container: {
    width: 430,
    height: 562,
    position: 'relative',
  },
  row: {
    position: 'absolute',
  },
  tinyTop: {
    top: 80,
    left: 110,
    right: 110,
    opacity: 0.15,
  },
  smallTop: {
    top: 155,
    left: 60,
    right: 60,
    opacity: 0.3,
  },
  main: {
    top: 211,
    left: 0,
    right: 0,
  },
  smallBottom: {
    top: 359,
    left: 60,
    right: 60,
    opacity: 0.3,
  },
  tinyBottom: {
    top: 434,
    left: 110,
    right: 110,
    opacity: 0.15,
  },
});

const styles = StyleSheet.create({
  card: {
    borderRadius: 20,
    overflow: 'hidden',
    width: 430,
    height: 562,
  },
  image: {
    width: '100%',
    height: '100%',
  },
});

export default Card;
